/* import __COLOCATED_TEMPLATE__ from './select-content-type.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import type IntlService from 'ember-intl/services/intl';
import {
  AI_AGENT_ENTITY_TYPES,
  AI_COPILOT_ENTITY_TYPES,
  FinAIType,
} from 'embercom/lib/knowledge-hub/constants';
import { EntityType } from 'embercom/models/data/entity-types';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { ViewId } from '../../setup-modal';
import type Store from '@ember-data/store';
import type ArticleGroup from 'embercom/models/articles/article-group';
import type ConversationExtractionService from 'embercom/services/conversation-extraction-service';
import type RouterService from '@ember/routing/router-service';
import type AddWebsiteModalService from 'embercom/services/add-website-modal-service';
import type AppService from 'embercom/services/app-service';
import type ModalService from 'embercom/services/modal-service';

interface Args {
  modal: any;
  title: string;
  onNavigationBack: () => void;
  onNavigationNext: (viewId: ViewId) => void;
  showBackButton: boolean;
  onModalClose: () => void;
  context?: {
    folder?: string;
    entityTypes?: EntityType[];
    aiTypeFilter?: FinAIType;
    includeNonKnowledgeBaseContent?: boolean;
    openInNewTab?: boolean;
    openInContext?: boolean;
    showAddContentTiles?: boolean;
    showImportSourcesTiles?: boolean;
    isStandaloneUnifiedContentSeeAll?: boolean;
    isConversationHistorySynced?: boolean;
  };
}

const CONTENT_ENTITY_TYPES = [
  EntityType.ArticleContent,
  EntityType.Answer,
  EntityType.InternalArticle,
  EntityType.ContentSnippet,
];

const STANDALONE_CONTENT_ENTITY_TYPES = [
  EntityType.Answer,
  EntityType.ContentSnippet,
  EntityType.InternalArticle,
];

const SYNC_ENTITY_TYPES = [
  EntityType.ArticleContent,
  EntityType.InternalArticle,
  EntityType.ExternalContent,
  EntityType.FileSourceContent,
];

const STANDALONE_ALLOWED_IMPORT_IDS = [
  'zendesk',
  'salesforce_knowledge',
  'conversation-history',
  'freshdesk',
  'notion',
  'confluence',
  'guru',
  'document360',
  'box',
  'file-upload',
  'hubspot',
];

type Tile = {
  id: string;
  title: string;
  entityType: EntityType;
  description: string;
};

export default class SelectContentType extends Component<Args> {
  @service declare router: RouterService;
  @service declare intercomEventService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare permissionsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare conversationExtractionService: ConversationExtractionService;
  @service declare addWebsiteModalService: AddWebsiteModalService;
  @service declare modalService: ModalService;
  get conversationExtractionSettings() {
    return this.conversationExtractionService.conversationExtractionSettings;
  }

  get shouldUseContentSetupV2() {
    return this.appService.app.canUseUnifiedContentUI;
  }

  get shouldShowImportSources() {
    return this.args.context?.showImportSourcesTiles ?? true;
  }

  get shouldShowAddContentTiles() {
    return this.args.context?.showAddContentTiles ?? !this.shouldUseContentSetupV2;
  }

  get isStandaloneUnifiedContentSeeAll() {
    return this.args.context?.isStandaloneUnifiedContentSeeAll === true;
  }

  get isConversationHistorySynced() {
    return this.args.context?.isConversationHistorySynced === true;
  }

  get shouldShowConversationExtractionSection() {
    return false;
  }

  get canUseHubSpot() {
    return (
      this.appService.app.canUseFeature('team-knowledge-foundations-hubspot-integration') ||
      this.appService.app.canUseFeature('team-knowledge-foundations-hubspot-react-importer')
    );
  }

  get shouldShowConversationHistory() {
    return this.appService.mainStandalonePlatform?.supportsConversationSyncing ?? false;
  }

  get conversationHistoryTitle() {
    let platformKey = this.appService.mainStandalonePlatform?.platformKey;
    if (platformKey === 'zendesk') {
      return this.intl.t(
        'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.zendesk-ticket-history.title',
      );
    } else if (platformKey === 'salesforce') {
      return this.intl.t(
        'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.salesforce-case-history.title',
      );
    } else if (platformKey === 'freshdesk') {
      return this.intl.t(
        'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.freshdesk-ticket-history.title',
      );
    }
    return '';
  }

  get conversationHistoryIconUrl() {
    let platformKey = this.appService.mainStandalonePlatform?.platformKey;
    let iconNameMap: Record<string, string> = {
      zendesk: 'zendesk',
      salesforce: 'salesforce_knowledge',
      freshdesk: 'freshdesk',
    };
    let iconName = platformKey ? iconNameMap[platformKey] : undefined;
    if (!iconName) {
      return undefined;
    }
    return assetUrl(`/assets/images/knowledge-hub/icons/${iconName}.png`);
  }

  get modalHeaderTitle() {
    if (this.shouldShowAddContentTiles) {
      return this.intl.t(
        'knowledge-hub.filterable-list.setup-modal.views.select-content.sections.content.title',
      );
    }
    return this.args.title;
  }

  get contentEntityTypes() {
    let entityTypesToDisplay = this.appService.app.canUseStandalone
      ? STANDALONE_CONTENT_ENTITY_TYPES.filter((entityType) => {
          return !this.isCopilotOnly(entityType) || this.appService.app.canUseCopilotOnStandalone;
        })
      : CONTENT_ENTITY_TYPES;
    return this.filterDisplayableEntityTypes(entityTypesToDisplay);
  }

  get syncEntityTypes() {
    let displayableValues = this.filterDisplayableEntityTypes(SYNC_ENTITY_TYPES);
    if (this.appService.app.hasStandalonePlatform('salesforce')) {
      return displayableValues.reject((entityType) => entityType === EntityType.ArticleContent);
    }
    return displayableValues;
  }

  get showConversationExtractionTile() {
    return false;
  }

  private filterDisplayableEntityTypes(entityTypesToDisplay: EntityType[]) {
    let entityTypes: EntityType[] | null = null;
    if (this.args.context?.aiTypeFilter === FinAIType.COPILOT) {
      entityTypes = AI_COPILOT_ENTITY_TYPES;
    } else if (this.args.context?.aiTypeFilter === FinAIType.AGENT) {
      entityTypes = AI_AGENT_ENTITY_TYPES;
    } else if (this.args.context?.entityTypes) {
      entityTypes = this.args.context?.entityTypes;
    }

    if (entityTypes) {
      entityTypesToDisplay = entityTypesToDisplay.filter((entityType) =>
        entityTypes?.includes(entityType),
      );
    }
    if (!this.args.context?.includeNonKnowledgeBaseContent) {
      entityTypesToDisplay = entityTypesToDisplay.filter(
        (entityType) => entityType !== EntityType.Answer,
      );
    }
    return entityTypesToDisplay;
  }

  private isCopilotOnly(entityType: EntityType) {
    let usageSummaryForEntity = this.knowledgeHubService.usageSummary?.[entityType];

    if (usageSummaryForEntity) {
      return (
        usageSummaryForEntity.is_eligible_for_copilot &&
        !usageSummaryForEntity.is_eligible_for_chatbot
      );
    }

    return false;
  }

  get contentTiles(): Tile[] {
    let localizationPrefix = 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles';

    let contentEntityTypes = [...this.contentEntityTypes];
    if (this.shouldShowAddContentTiles && !this.shouldShowImportSources) {
      contentEntityTypes.push(EntityType.FileSourceContent);
    }

    return contentEntityTypes.map((entityType) => {
      let titleKey = `${localizationPrefix}.${objectNames[entityType]}.title`;
      let descriptionKey = this.appService.app.canUseStandalone
        ? `${localizationPrefix}.${objectNames[entityType]}.description-ai-agent`
        : `${localizationPrefix}.${objectNames[entityType]}.description`;

      // Use standalone-specific translations for EntityType.ArticleContent (120) when in standalone mode
      if (this.appService.app.canUseStandalone && entityType === EntityType.ArticleContent) {
        titleKey = `${localizationPrefix}.${objectNames[entityType]}.standalone-title`;
        descriptionKey = `${localizationPrefix}.${objectNames[entityType]}.standalone-description-ai-agent`;
      }

      return {
        id: `${entityType}`,
        title: this.intl.t(titleKey),
        entityType,
        description: this.intl.t(descriptionKey),
      };
    });
  }

  get importTiles() {
    let tiles = [
      {
        id: 'zendesk',
        viewId: 'zendesk-sync',
        title: this.intl.t(
          this.isStandaloneUnifiedContentSeeAll
            ? 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.zendesk.standalone-title'
            : 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.zendesk.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/zendesk.png') as
          | string
          | undefined,
        entityType: EntityType.ArticleContent,
        showSyncedDot: undefined as boolean | undefined,
      },
      {
        id: 'guru',
        viewId: 'guru-sync',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.guru.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/guru.png'),
        entityType: EntityType.InternalArticle,
      },
      {
        id: 'notion',
        viewId: 'notion-sync',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.notion.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/notion.png'),
        entityType: EntityType.InternalArticle,
      },
      {
        id: 'confluence',
        viewId: 'confluence-sync',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.confluence.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/confluence.png'),
        entityType: EntityType.InternalArticle,
      },
      {
        id: 'external-content',
        viewId: 'external-content',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.external-content.title',
        ),
        entityType: EntityType.ExternalContent,
      },
      {
        id: 'file-upload',
        viewId: 'file-upload',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.file.title',
        ),
        entityType: EntityType.FileSourceContent,
      },
    ].filter((tile) => {
      // Only show external-content tile if feature flag is off
      if (tile.id === 'external-content') {
        return !this.shouldUseContentSetupV2;
      }
      return true;
    });
    if (!this.appService.app.is_fin_standalone_app) {
      tiles.push({
        id: 'salesforce_knowledge',
        viewId: 'salesforce-sync',
        title: this.intl.t(
          this.isStandaloneUnifiedContentSeeAll
            ? 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.salesforce.standalone-title'
            : 'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.salesforce.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/salesforce_knowledge.png'),
        entityType: EntityType.FileSourceContent,
      });
    }
    // Always show box tile
    tiles.push({
      id: 'box',
      viewId: 'box-sync',
      title: this.intl.t(
        'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.box.title',
      ),
      sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/box.png'),
      entityType: EntityType.FileSourceContent,
    });
    tiles.push({
      id: 'document360',
      viewId: 'document360-sync',
      title: this.intl.t(
        'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.document360.title',
      ),
      sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/document360.png'),
      entityType: EntityType.FileSourceContent,
    });

    // Show Freshdesk tile
    // For standalone apps, require it to be a Freshdesk workspace
    if (
      !this.appService.app.isStandaloneApp ||
      this.appService.app.hasStandalonePlatform?.('freshdesk')
    ) {
      tiles.push({
        id: 'freshdesk',
        viewId: 'freshdesk-sync',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.freshdesk.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/freshdesk.png'),
        entityType: EntityType.FileSourceContent,
      });
    }

    if (this.appService.app.canUseFeature('answerbot-fin-commerce-structured-search')) {
      tiles.push({
        id: 'shopify',
        viewId: 'shopify-sync',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.shopify.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/shopify.svg'),
        entityType: EntityType.ArticleContent,
      });
    }

    if (this.canUseHubSpot) {
      tiles.push({
        id: 'hubspot',
        viewId: 'hubspot-sync',
        title: this.intl.t(
          'knowledge-hub.filterable-list.setup-modal.views.select-content.tiles.hubspot.title',
        ),
        sourceIconUrl: assetUrl('/assets/images/knowledge-hub/icons/hubspot.svg'),
        entityType: EntityType.ArticleContent,
      });
    }

    // Show conversation history tile only for standalone unified content "See all" modal
    // when the platform supports conversation syncing (Zendesk or Salesforce)
    if (
      this.isStandaloneUnifiedContentSeeAll &&
      this.appService.mainStandalonePlatform?.supportsConversationSyncing
    ) {
      tiles.push({
        id: 'conversation-history',
        viewId: 'conversation-history-sync',
        title: this.conversationHistoryTitle,
        sourceIconUrl: this.conversationHistoryIconUrl,
        entityType: EntityType.Conversation,
        showSyncedDot: this.isConversationHistorySynced,
      });
    }

    // For standalone unified content "See all" modal, return with ordered tiles
    if (this.isStandaloneUnifiedContentSeeAll) {
      return tiles
        .filter((tile) => {
          if (!STANDALONE_ALLOWED_IMPORT_IDS.includes(tile.id)) {
            return false;
          }
          if (tile.id === 'file-upload') {
            return this.appService.app.canUseCopilotOnStandalone;
          }
          if (tile.id === 'zendesk') {
            return this.appService.app.hasStandalonePlatform?.('zendesk');
          }
          if (tile.id === 'conversation-history') {
            return this.shouldShowConversationHistory;
          }
          if (tile.id === 'salesforce_knowledge') {
            return this.appService.app.hasStandalonePlatform?.('salesforce');
          }
          if (tile.id === 'freshdesk') {
            return this.appService.app.hasStandalonePlatform?.('freshdesk');
          }
          if (tile.id === 'document360') {
            return this.appService.app.canUseDocument360Standalone;
          }
          if (tile.id === 'box') {
            return this.appService.app.canUseBoxStandalone;
          }
          if (tile.id === 'hubspot') {
            return this.canUseHubSpot;
          }
          return true;
        })
        .sort(
          (a, b) =>
            STANDALONE_ALLOWED_IMPORT_IDS.indexOf(a.id) -
            STANDALONE_ALLOWED_IMPORT_IDS.indexOf(b.id),
        );
    }

    return tiles.filter((tile) => this.syncEntityTypes.includes(tile.entityType));
  }

  @action async navigateToConversationExtraction() {
    await this.navigateToView('conversation-extraction', EntityType.ContentSnippet);
  }

  @action async navigateToView(viewId: ViewId, entityType: EntityType) {
    try {
      await this.knowledgeHubService.ensurePermissions(entityType);
    } catch (error) {
      this.args.onModalClose();
      return;
    }

    if (viewId === 'zendesk-sync') {
      this.args.onModalClose();
      this.openExternalSourceSheet('zendesk');
      return;
    }

    if (viewId === 'confluence-sync' && this.appService.app.canUseConfluenceReactImporter) {
      this.args.onModalClose();
      this.openExternalSourceSheet('confluence');
      return;
    }

    if (viewId === 'notion-sync' && this.appService.app.canUseNotionReactImporter) {
      this.args.onModalClose();
      this.openExternalSourceSheet('notion');
      return;
    }

    if (viewId === 'guru-sync' && this.appService.app.canUseGuruReactImporter) {
      this.args.onModalClose();
      this.openExternalSourceSheet('guru');
      return;
    }

    if (
      viewId === 'hubspot-sync' &&
      !this.appService.app.canUseFeature('team-knowledge-foundations-hubspot-integration') &&
      this.appService.app.canUseFeature('team-knowledge-foundations-hubspot-react-importer')
    ) {
      this.args.onModalClose();
      this.openExternalSourceSheet('hubspot');
      return;
    }

    if (viewId === 'salesforce-sync') {
      this.args.onModalClose();

      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: {
          openSalesforceSync: true,
        },
      });
      return;
    }

    if (viewId === 'conversation-history-sync') {
      this.args.onModalClose();
      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: {
          openConversationIngestionDrawer: true,
        },
      });
      return;
    }

    if (entityType === EntityType.ExternalContent) {
      this.args.onModalClose();
      this.addWebsiteModalService.openAddWebsiteSheet();
      return;
    }

    this.args.onNavigationNext(viewId);
  }

  @action async createContent(entityType: EntityType) {
    let collections = (await this.store.findAll('articles/article-group'))
      .toArray()
      .filter((el: ArticleGroup) => el.id !== 'home');

    if (entityType === EntityType.ArticleContent && collections.length === 0) {
      this.args.onNavigationNext('set-up-help-center');
      return;
    }
    this.knowledgeHubService.createContent(
      entityType,
      { folder_id: this.args.context?.folder },
      this.args.context?.openInNewTab,
      this.args.context?.openInContext,
    );
    this.args.onModalClose();
  }

  @action async onClickAddContentTile(tile: Tile) {
    if (tile.entityType === EntityType.FileSourceContent) {
      await this.navigateToView('file-upload', tile.entityType);
    } else {
      this.createContent(tile.entityType);
    }
  }

  @action
  openExternalSourceSheet(sourceId: string) {
    this.modalService.openModal(
      'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
      {
        open: true,
        appId: this.appService.app.id,
        adminId: this.appService.app.currentAdmin.id.toString(),
        initialSourceId: sourceId,
        onClose: () => this.modalService.closeModal(),
      },
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SelectContentType': typeof SelectContentType;
    'knowledge-hub/setup-modal/select-content-type': typeof SelectContentType;
  }
}
